import type {
  ComparisonModelSelection,
  ComparisonModelSelections,
  ResearchComparisonEvent,
  ResearchComparisonWorkspace,
  ResearchVariant,
  SavedResearchComparisonRun,
  VariantEvaluation,
  VariantRunState,
} from '../types/localResearchComparison';

const MODEL_PRESET = /^[a-z0-9][a-z0-9._-]{0,63}$/;

/**
 * Das Modell, mit dem eine gespeicherte Seite tatsächlich lief. Maßgeblich ist
 * die Herkunft des Laufs, nicht der Wunsch der damaligen Anfrage.
 */
export const modelSelectionFromProvenance = (
  provenance: unknown,
): ComparisonModelSelection | undefined => {
  if (typeof provenance !== 'object' || provenance === null) return undefined;
  const record = provenance as Record<string, unknown>;
  if (record.researchLane !== 'B') return undefined;
  // Ohne Ergebnis ist nur die damalige Wahl bekannt, kein antwortendes Modell.
  const lane = record.modelLane ?? record.requestedModelLane;
  if (lane === 'local') return { lane: 'local' };
  if (lane === 'gemini') return { lane: 'gemini' };
  if (lane !== 'cortecs') return undefined;
  const preset = record.modelLane ? record.modelPreset : record.requestedModelPreset;
  return typeof preset === 'string' && MODEL_PRESET.test(preset)
    ? { lane: 'cortecs', preset }
    : { lane: 'cortecs' };
};

export const emptyComparisonRunState = (): VariantRunState => ({
  status: 'idle',
  statusMessage: 'Bereit für eine Vergleichsfrage',
  answer: '',
  tools: [],
  elapsedMs: null,
});

export const emptyComparisonEvaluation = (): VariantEvaluation => ({ accuracy: null, relevance: null, citations: null, notes: '' });

export const createComparisonWorkspace = (clientId: string): ResearchComparisonWorkspace => ({
  clientId,
  question: '',
  selection: 'both',
  submittedQuestion: '',
  runId: null,
  startedAt: null,
  runError: null,
  runStorageState: 'pending',
  isRunning: false,
  executedVariants: [],
  models: { A: { lane: 'gemini' }, B: { lane: 'gemini' } },
  executedModels: {},
  states: { A: emptyComparisonRunState(), B: emptyComparisonRunState() },
  evaluations: { A: emptyComparisonEvaluation(), B: emptyComparisonEvaluation() },
  preference: null,
  feedbackSaveState: 'idle',
  feedbackSavedAt: null,
  feedbackSaveError: null,
});

export const restoreComparisonWorkspace = (saved: SavedResearchComparisonRun): ResearchComparisonWorkspace => {
  const variants = saved.variants.length > 0
    ? saved.variants
    : Object.keys(saved.results).filter((value): value is ResearchVariant => value === 'A' || value === 'B');
  const states = { A: emptyComparisonRunState(), B: emptyComparisonRunState() };
  const executedModels: ComparisonModelSelections = {};
  for (const variant of variants) {
    const result = saved.results[variant];
    if (!result) continue;
    const selection = modelSelectionFromProvenance(result.result?.provenance);
    if (selection) executedModels[variant] = selection;
    states[variant] = {
      status: result.status,
      statusMessage: result.status === 'done' ? 'Gespeicherte Recherche' : 'Gespeicherter Lauf',
      answer: result.answer,
      tools: result.tools,
      ...(result.result ? { result: result.result } : {}),
      ...(result.error ? { error: result.error } : {}),
      elapsedMs: result.elapsedMs,
    };
  }
  return {
    ...createComparisonWorkspace(`saved:${saved.runId}`),
    question: saved.userQuery,
    selection: variants.length === 2 ? 'both' : variants[0] ?? 'both',
    submittedQuestion: saved.userQuery,
    runId: saved.runId,
    startedAt: saved.startedAt,
    runStorageState: 'confirmed',
    executedVariants: variants,
    models: executedModels,
    executedModels,
    states,
    evaluations: saved.evaluations,
    preference: saved.preference,
    feedbackSaveState: saved.feedbackUpdatedAt ? 'saved' : 'idle',
    feedbackSavedAt: saved.feedbackUpdatedAt,
  };
};

export const applyComparisonEvent = (
  workspace: ResearchComparisonWorkspace,
  event: ResearchComparisonEvent,
): ResearchComparisonWorkspace => {
  if (event.type === 'run.started') {
    return {
      ...workspace,
      runId: event.runId,
      startedAt: event.startedAt,
      submittedQuestion: event.userQuery,
      executedVariants: event.variants.length > 0 ? [...event.variants] : workspace.executedVariants,
      // Womit der Lauf wirklich begonnen hat, sagt der Server -- nicht der
      // Entwurf, den die Oberfläche noch im Auswahlfeld stehen hat.
      executedModels: { ...event.models },
    };
  }
  if (event.type === 'run.error') {
    const states = Object.fromEntries(Object.entries(workspace.states).map(([variant, state]) => [variant,
      state.status === 'running' || state.status === 'queued'
        ? { ...state, status: 'error', error: { code: event.code, message: event.message } }
        : state,
    ])) as Record<ResearchVariant, VariantRunState>;
    return { ...workspace, ...(event.runId ? { runId: event.runId } : {}), runStorageState: 'unavailable', runError: `${event.message} (${event.code})`, isRunning: false, states };
  }
  if (event.type === 'run.done') return { ...workspace, runId: event.runId, runStorageState: 'confirmed', isRunning: false };

  const variant = event.variant;
  const current = workspace.states[variant];
  let next: VariantRunState;
  if (event.type === 'variant.status') next = { ...current, status: 'running', statusMessage: event.message, elapsedMs: event.elapsedMs };
  else if (event.type === 'variant.chunk') next = { ...current, status: 'running', answer: current.answer + event.text, elapsedMs: event.elapsedMs };
  else if (event.type === 'variant.answer_reset') next = { ...current, status: 'running', answer: event.text, elapsedMs: event.elapsedMs };
  else if (event.type === 'variant.tools') next = { ...current, status: 'running', tools: [...current.tools, event.tools], elapsedMs: event.elapsedMs };
  else if (event.type === 'variant.done') next = { ...current, status: 'done', statusMessage: 'Recherche abgeschlossen', answer: event.result.answer, result: event.result, elapsedMs: event.elapsedMs };
  else next = {
    ...current,
    // Text mit falscher Modellidentität gehört weder in die Karte noch in den Export.
    ...(event.code === 'ENGINE_IDENTITY_MISMATCH' ? { answer: '', tools: [], result: undefined } : {}),
    status: 'error', error: { code: event.code, message: event.message, diagnostic: event.diagnostic }, elapsedMs: event.elapsedMs,
  };
  return { ...workspace, states: { ...workspace.states, [variant]: next } };
};
